// Team tools: list the user's teams, switch the app between them, copy an agent from one to another
import { tool } from "ai"
import { z } from "zod"
import { supabase } from "@/lib/agents/db"
import type { ApprovalRequest } from "@/lib/assistant/types"

export interface TeamSummary { id: string; name: string; description?: string | null }

// Parameters that usually point at something owned by one team: its knowledge, forms, templates or connections
const TEAM_BOUND_PARAM = /(collection|corpus|vector|kb|knowledge|source|form|template|channel|connector|api_?key|token|project)/i

// Worker parameters in the copy that may still point at the source team's resources
function teamBoundWorkers(workers: any[]): string[] {
  const out: string[] = []
  for (const w of workers ?? []) {
    const keys = Object.entries(w?.parameters ?? {})
      .filter(([k, v]) => TEAM_BOUND_PARAM.test(k) && v != null && v !== "" && !(Array.isArray(v) && !v.length))
      .map(([k]) => k)
    if (keys.length) out.push(`${w.type} (${w.id}): ${keys.join(", ")}`)
  }
  return out
}

export interface TeamsContext {
  currentTeamId: () => string | undefined
  teams: () => TeamSummary[]
  // Switches the whole app, and keeps this chat and the running reply going
  switchTeam: (team: TeamSummary) => Promise<void>
  ask: (req: ApprovalRequest) => Promise<boolean>
}

export function makeTeamsTools(ctx: TeamsContext) {
  const findTeam = (idOrName: string) => {
    const q = idOrName.trim().toLowerCase()
    const teams = ctx.teams()
    return teams.find(t => t.id === idOrName.trim())
      ?? teams.find(t => t.name?.toLowerCase() === q)
      ?? (teams.filter(t => t.name?.toLowerCase().includes(q)).length === 1 ? teams.find(t => t.name?.toLowerCase().includes(q)) : undefined)
  }

  const tools = {
    team_list: tool({
      description: "The teams the user belongs to, and which one the app is working in now.",
      parameters: z.object({}),
      execute: async () => ({
        current: ctx.currentTeamId() ?? null,
        teams: ctx.teams().map(t => ({ id: t.id, name: t.name, description: t.description ?? null, current: t.id === ctx.currentTeamId() })),
      }),
    }),

    team_switch: tool({
      description:
        "Switch the whole app to another of the user's teams, like picking it in the sidebar. Everything after this works in that team: "
        + "pages, agents, the Smart CMS and your tools. This chat carries on.",
      parameters: z.object({ team: z.string().describe("Team id, or its exact name from team_list") }),
      execute: async ({ team }) => {
        const target = findTeam(team)
        if (!target) return { error: `The user isn't in a team called "${team}". Check team_list.` }
        if (target.id === ctx.currentTeamId()) return { ok: true, unchanged: true, team: target.name }
        await ctx.switchTeam(target)
        return { ok: true, team: { id: target.id, name: target.name } }
      },
    }),

    agent_copy_to_team: tool({
      description:
        "Copy an agent into another of the user's teams, as a new agent. The original is untouched. Always asks the user first. "
        + "The copy leaves out things that belong to the source team: its bound form, evaluation items and integration tokens. "
        + "It reports workers whose settings may still point at the source team's resources, so they can be checked.",
      parameters: z.object({
        agentId: z.number().describe("The agent to copy, from a team the user is in"),
        targetTeam: z.string().describe("Team id, or its exact name from team_list"),
        title: z.string().nullable().describe("Title for the copy, or null for '<original title> (copy)'"),
      }),
      execute: async ({ agentId, targetTeam, title }) => {
        const target = findTeam(targetTeam)
        if (!target) return { error: `The user isn't in a team called "${targetTeam}". Check team_list.` }
        const memberOf = new Set(ctx.teams().map(t => t.id))

        const { data: src, error } = await supabase.from("agents").select("*").eq("id", agentId).maybeSingle()
        if (error) return { error: error.message }
        if (!src || !memberOf.has(src.team_id as string)) return { error: `No agent ${agentId} in any of the user's teams.` }
        const fromTeam = ctx.teams().find(t => t.id === src.team_id)

        const workers = (Array.isArray(src.workers) ? src.workers : []) as any[]
        const config: any = { ...((src.config as any) ?? {}) }
        const dropped: string[] = []
        if (config.forms?.length) dropped.push("the bound Magic Form")
        if (config.evalItems?.length) dropped.push(`${config.evalItems.length} evaluation item${config.evalItems.length === 1 ? "" : "s"}`)
        if (config.integrations && Object.values(config.integrations).some(Boolean)) dropped.push("integration tokens")
        config.forms = []
        config.evalItems = []
        config.integrations = {}
        const check = teamBoundWorkers(workers)
        const newTitle = title?.trim() || `${src.title || "Untitled"} (copy)`

        const ok = await ctx.ask({
          summary: `Copy agent "${src.title || "Untitled"}" from ${fromTeam?.name ?? "its team"} to ${target.name}`,
          detail: [
            `New agent: ${newTitle}`,
            `${workers.length} workers, ${Object.keys((src.edges as any) ?? {}).length} connections`,
            dropped.length ? `Left out (they belong to ${fromTeam?.name ?? "the source team"}): ${dropped.join(", ")}` : "",
            check.length ? `Check after copying, these may point at the source team:\n${check.join("\n")}` : "",
          ].filter(Boolean).join("\n\n"),
        })
        if (!ok) return { error: "Rejected by reviewer." }

        const { data: created, error: insertError } = await supabase.from("agents").insert({
          title: newTitle,
          description: src.description,
          type: src.type,
          status: src.status,
          workers: src.workers,
          edges: src.edges,
          config,
          team_id: target.id,
          debuguuid: crypto.randomUUID(),
          versions: [],
          fork_id: null,
          fork_base: null,
        } as any).select("id, title").single()
        if (insertError) return { error: insertError.message }

        return {
          ok: true,
          agent: { id: created.id, title: created.title, team: target.name, page: `/agent/${created.id}` },
          leftOut: dropped,
          check,
          note: target.id === ctx.currentTeamId()
            ? undefined
            : `The copy is in ${target.name}. Switch to that team with team_switch before opening it.`,
        }
      },
    }),
  }

  return tools
}
